import { cn } from '../../utils/cn';
import type { KeyboardEvent } from 'react';

export interface SegmentedControlOption<V extends string = string> {
  readonly value: V;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SegmentedControlProps<V extends string = string> {
  readonly value: V;
  readonly onChange: (value: V) => void;
  readonly options: readonly SegmentedControlOption<V>[];
  readonly size?: 'sm' | 'md';
  readonly fullWidth?: boolean;
  readonly className?: string;
  readonly 'aria-label'?: string;
}

const SIZE = {
  sm: 'min-h-9 text-small [&_button]:px-3',
  md: 'min-h-10 text-small [&_button]:px-4',
} as const;

export function SegmentedControl<V extends string = string>({
  value,
  onChange,
  options,
  size = 'md',
  fullWidth = false,
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<V>) {
  const enabled = options.filter((option) => !option.disabled);
  const tabStop = enabled.find((option) => option.value === value)?.value ?? enabled[0]?.value;
  const handleKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key))
      return;
    const group = event.currentTarget.closest('[role="radiogroup"]');
    if (!group) return;
    const radios = Array.from(
      group.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)'),
    );
    const index = radios.indexOf(event.currentTarget);
    const rtl = getComputedStyle(group).direction === 'rtl';
    const backwards = event.key === 'ArrowUp' || event.key === (rtl ? 'ArrowRight' : 'ArrowLeft');
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? radios.length - 1
          : (index + (backwards ? -1 : 1) + radios.length) % radios.length;
    event.preventDefault();
    radios[nextIndex]?.focus();
    radios[nextIndex]?.click();
  };
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex max-w-full min-w-0 overflow-x-auto p-1 rounded-lg bg-surface-2 border border-hairline',
        fullWidth && 'w-full',
        SIZE[size],
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={opt.disabled}
            tabIndex={!opt.disabled && opt.value === tabStop ? 0 : -1}
            onClick={() => onChange(opt.value)}
            onKeyDown={handleKey}
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-md font-semibold whitespace-nowrap [@media(pointer:coarse)]:min-h-10',
              'transition-[color,background-color,box-shadow] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none',
              'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-text',
              fullWidth && 'flex-1',
              selected
                ? 'bg-surface text-ink shadow-[0_1px_0_rgba(0,0,0,0.04)]'
                : 'text-ink-2 hover:text-ink',
              opt.disabled && 'opacity-45 cursor-not-allowed',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
